/**
 * LacreSelector — Multi-select for available lacres.
 */
import React, { useState, useEffect } from 'react';
import { LacreDisponivel } from '../../services/conferenciaService';
import { Tag, Check } from 'lucide-react';

interface Props {
  lacres: LacreDisponivel[];
  onSelect: (lacres: string[]) => Promise<void>;
  readOnly: boolean;
}

export const LacreSelector: React.FC<Props> = ({ lacres, onSelect, readOnly }) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(lacres.filter(l => l.ja_selecionado).map(l => l.lacre))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(new Set(lacres.filter(l => l.ja_selecionado).map(l => l.lacre)));
  }, [lacres]);

  const toggle = (lacre: string) => {
    if (readOnly) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(lacre)) next.delete(lacre);
      else next.add(lacre);
      return next;
    });
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      await onSelect(Array.from(selected));
    } finally {
      setSaving(false);
    }
  };

  // Check if selection changed from initial
  const initialSelected = new Set(lacres.filter(l => l.ja_selecionado).map(l => l.lacre));
  const hasChanges = selected.size !== initialSelected.size ||
    [...selected].some(l => !initialSelected.has(l));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Selecionar Lacre(s)
        </h4>
        {hasChanges && !readOnly && (
          <button
            onClick={handleApply}
            disabled={saving || selected.size === 0}
            className="px-4 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Aplicando...' : `Aplicar (${selected.size})`}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {lacres.map((lacre) => {
          const isSelected = selected.has(lacre.lacre);
          return (
            <button
              key={lacre.lacre}
              onClick={() => toggle(lacre.lacre)}
              disabled={readOnly}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-300'
              } disabled:cursor-not-allowed`}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                isSelected
                  ? 'bg-blue-500 border-blue-500'
                  : 'border-gray-300 dark:border-gray-500'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              {lacre.lacre}
              {lacre.ordem_coleta && (
                <span className="text-xs text-gray-400 ml-1">({lacre.ordem_coleta})</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
